/**
 * One flight in the list: where it goes, what it is flown with, when, and how
 * far — and nothing else. No version number, no "changed 3 minutes ago", no
 * waypoint count: none of them is a reason to open a flight rather than the one
 * beside it.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PlanSummary } from '../api';
import { dateToIso, formatDate, parseDateParts } from '../fields';
import { useT } from '../i18n';
import { routeTotalNm } from '../route';
import { IconMore } from './icons';

export interface FlightAction {
  key: string;
  label: string;
  danger?: boolean;
  run(): void;
}

/** `17.08.` — the day, in the sheet's own notation, without the year noise. */
function shortDate(datum: string | null, todayIso: string, todayLabel: string): string {
  if (!datum) return '';
  if (dateToIso(datum) === todayIso) return todayLabel;
  const p = parseDateParts(datum);
  return p ? formatDate(p).replace(/\.\d{4}$/, '.') : datum;
}

export function FlightRow({
  plan,
  registration,
  actions,
  todayIso,
}: {
  plan: PlanSummary;
  registration?: string | undefined;
  actions: readonly FlightAction[];
  todayIso: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const route = [plan.von, plan.nach].filter(Boolean).join(' → ');
  const nm = routeTotalNm(plan.route);
  const date = shortDate(plan.datum, todayIso, t('flights.today'));

  return (
    <div className="ae-flightrow">
      <div className="ae-flight-main">
        <Link
          className={'ae-flight-route' + (route ? '' : ' none')}
          to={'/flights/' + plan.id}
        >
          {route || t('flights.noRoute')}
        </Link>
        <div className="ae-flight-meta">
          {plan.is_template ? <span className="ae-chip">{t('flights.template')}</span> : null}
          {registration ? <span>{registration}</span> : null}
          {date ? <span className={date === t('flights.today') ? undefined : 'ae-num'}>{date}</span> : null}
          {/* the title only earns a place when it says something the route does not */}
          {plan.title && plan.title !== route ? <span>{plan.title}</span> : null}
        </div>
      </div>
      {nm > 0 ? (
        <div className="ae-flight-sum">
          <span className="ae-num">{nm < 10 ? nm.toFixed(1) : String(Math.round(nm))}</span>
          <span className="ae-summary-unit">NM</span>
        </div>
      ) : null}
      <div className="ae-flight-acts ae-menuwrap" ref={wrap}>
        <button
          type="button"
          className="ae-btn ae-btn-icon"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t('flights.menu')}
          onClick={() => setOpen(v => !v)}
        >
          <IconMore />
        </button>
        {open ? (
          <div className="ae-menu" role="menu">
            {actions.map(a => (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                className={a.danger ? 'danger' : undefined}
                onClick={() => {
                  setOpen(false);
                  a.run();
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
